/**
 * Portal-owned copy of shared-ui's gradient helper (src/utils, v0.5.383).
 *
 * It is ~30 lines, but importing it from the package put the whole library on
 * the login path. The portal owns everything a page needs before the store is
 * on screen; the store surface keeps importing shared-ui.
 */

export enum EGradientDirection {
  TopToBottom = 'to bottom',
  BottomToTop = 'to top',
  LeftToRight = 'to right',
  RightToLeft = 'to left',
  TopLeftToBottomRight = 'to bottom right',
  BottomRightToTopLeft = 'to top left',
  TopRightToBottomLeft = 'to bottom left',
  BottomLeftToTopRight = 'to top right',
}

export interface Gradient {
  colorOne: string;
  colorTwo?: string;
  // Plain string is accepted because callers hand over API data typed as
  // string (e.g. packages/types' Gradient); createGradientOrSolid validates
  // the value and falls back, exactly as the shared-ui original does.
  gradientDirection?: EGradientDirection | string;
}

const hasSecondColor = (colorTwo?: string): colorTwo is string =>
  Boolean(colorTwo) && colorTwo !== 'false';

export const createGradientOrSolid = (
  gradient: Gradient | undefined,
  isTripleGradient = false
): string => {
  if (gradient === undefined || gradient?.colorOne === undefined) {
    return 'white';
  }

  const { colorOne, colorTwo, gradientDirection } = gradient;

  if (colorOne && !hasSecondColor(colorTwo)) {
    return colorOne;
  }

  // Validate gradientDirection and provide fallback
  const validGradientDirection =
    gradientDirection &&
    Object.values(EGradientDirection).includes(gradientDirection as EGradientDirection)
      ? gradientDirection
      : EGradientDirection.BottomToTop;

  if (isTripleGradient) {
    return `linear-gradient(${validGradientDirection}, ${colorOne}, ${colorTwo}, ${colorOne})`;
  }

  return `linear-gradient(${validGradientDirection}, ${colorOne}, ${colorTwo})`;
};
